/**
 * VFX render subsystem. (§6.7 — "the satisfying details")
 *
 * Owns the goal nets, the ball trail, grass spray, and rain. All of it is
 * DOWNSTREAM of the sim (§15.2): we only READ `RenderState`, and — being
 * graphics — `Math.*` and the wall clock are fair game here. Nothing in this
 * file can change a match outcome.
 *
 * Two hard rules shape every line below:
 *   1. ZERO ALLOCATION in `update()` (§15.7). Every buffer, every scratch
 *      vector and the whole verlet state are allocated up front (in the
 *      constructor / `onPitchConfig`) and mutated in place. A stray `{}`/`[]`
 *      on the 60Hz path is a GC stutter you can see.
 *   2. The Low tier must hold 60fps on Intel integrated graphics (§6.8). Every
 *      effect scales its live count / substeps off the quality tier, and the
 *      screen-space wetness cue is disabled entirely below Medium. Counts are
 *      pre-sized to the Ultra maximum and effects draw a sub-range, so a tier
 *      change never reallocates a GPU buffer.
 *
 * Draw-call cost (all tiers, wet + fast shot on screen): 3 net draws
 * (2 verlet back-roof sheets + 1 merged static side-panel mesh) + 1 ball
 * trail + 1 grass Points + 1 rain LineSegments + 1 wetness quad = 8 draws,
 * ~1.3k triangles. Trivially inside the §6.8 share (150 draws / 2.5M tris).
 *
 * The headline is the NETS. A ball hitting the back of the net and the net
 * bulging is, per §6.7, "one of the most satisfying details in football," so
 * the verlet cloth gets the real engineering and the lesser effects stay cheap.
 *
 * D9 honesty: there is no rigged/mocap player art here and none is needed —
 * VFX attaches to positions the seam already provides (ball, feet). The one
 * thing I synthesize rather than author is texture: the net weave, the rain
 * droplet sheet and the grass fleck are all generated in-code (canvas /
 * procedural), never downloaded (§16). Assets I'd genuinely want are listed in
 * the hand-off report, not faked.
 */

import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  CanvasTexture,
  Color,
  DoubleSide,
  DynamicDrawUsage,
  CylinderGeometry,
  Group,
  LinearFilter,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  NormalBlending,
  PlaneGeometry,
  Points,
  Quaternion,
  RepeatWrapping,
  ShaderMaterial,
  SRGBColorSpace,
  Vector3,
  type Object3D,
  type Texture,
} from 'three';
import {
  BALL_RADIUS,
  GOAL_DEPTH,
  GOAL_HEIGHT,
  GOAL_POST_RADIUS,
  GOAL_WIDTH,
  GRAVITY,
  MAX_PLAYERS,
  SHOT_MAX_SPEED,
  type MatchConfig,
} from '@fg/shared/sim';
import {
  QUALITY_RANK,
  type QualitySettings,
  type RenderContext,
  type RenderState,
  type RenderSubsystem,
} from './context.js';

// ---------------------------------------------------------------------------
// Tunables — VFX-local, cosmetic, never touched by the sim. (§6.7)
// ---------------------------------------------------------------------------

/** Net verlet grid: brief says "a low-res 20×15 verlet grid" (§6.7). COLS runs
 *  across the goal width, ROWS along the drape (crossbar → roof → back → floor). */
const NET_COLS = 20;
const NET_ROWS = 15;
/** The net keeps its designed drape shape via a weak pull toward rest each step,
 *  so a bulge always relaxes back and gravity never makes the sheet sag forever. */
const NET_SHAPE_MEMORY = 0.06;
/** Verlet damping per step (velocity retention). */
const NET_DAMPING = 0.97;
/** Constraint relaxation iterations per substep — 2 is plenty for a cosmetic net. */
const NET_ITERATIONS = 2;
/** Gentle gravity on the net; it is pinned on all four borders so this only
 *  billows the interior, it does not need to be the real −9.81. */
const NET_GRAVITY = -3.0;
/** Radius (m) of nodes the ball disturbs, and how hard it pushes them out. */
const NET_BALL_RADIUS = 1.25;
const NET_BULGE_BASE = 0.22; // m at low ball speed
const NET_BULGE_PER_SPEED = 0.018; // extra metres of bulge per m/s of ball speed
const NET_BULGE_MAX = 0.75; // m
/** How fast disturbed nodes chase the bulge target while the ball is in-goal. */
const NET_PUSH_LERP = 0.4;
/** One-shot outward snap the instant the ball enters the goal volume. */
const NET_ENTRY_IMPULSE = 0.12; // m, scaled by ball speed

/**
 * Goal-frame presentation. These deliberately match the simulation's post
 * radius, so the visible frame and the collision frame can never disagree.
 */
const GOAL_FRAME_COLOR = 0xfafcff;

/** Ball trail only on genuine strikes, never on passes (§6.7). */
const TRAIL_SPEED_ON = 18; // m/s — start drawing above this
const TRAIL_SPEED_OFF = 12; // m/s — hysteresis; fully faded below this
const TRAIL_MIN_SPACING = 0.14; // m between ribbon samples
const TRAIL_MAX_SAMPLES = 32;
const TRAIL_HALF_WIDTH = 0.09; // m at the head, tapers to 0 at the tail
const TRAIL_FADE_UP = 6.0; // alpha units/s toward 1 while fast
const TRAIL_FADE_DOWN = 4.0; // alpha units/s toward 0 once slow

/**
 * Owner, 2026-08-15: "dont show the effect once the kick is completed. once
 * the animation is over its done until another player do that". `powerKick`
 * only ever changes on the NEXT strike (applyStrike overwrites it, kicked or
 * not) — a receiving player who traps and dribbles the ball never strikes it,
 * so the flag would otherwise sit true indefinitely and the red line/thunder
 * would still be showing over a ball that has plainly finished its flight and
 * come to rest at someone's feet. This is the floor that makes the render
 * layer stop on its own the moment the ball is actually done moving, rather
 * than waiting on a sim event that may never come before the next kick.
 */
const POWER_EFFECT_MIN_SPEED_M_S = 1.0;

/**
 * Power-kick trail + lightning. Iterated twice from the owner's own testing:
 * first an expanding-ring "wave" (rejected: "stop the rings please show the
 * trial like aline"), then this — reference image attached, a ball trailing
 * a bright flowing ribbon with electric arcs woven through it.
 *
 * THE LINE IS NOT A NEW MESH. It is the existing ball trail ribbon just
 * above (`buildTrail`/`updateTrail`), recoloured and re-gated when
 * `state.ball.powerKick` is true (mirrors sim's `BallState.powerKick` — a
 * boost-tier-2 kick OR a full-charge one) — one ribbon, two looks, so a
 * power kick's trail and an ordinary fast shot's pale trail can never
 * disagree about where the ball has actually been.
 *
 * THE THUNDER is a small pool of independent sparks, spawned continuously
 * at the ball's CURRENT position for as long as `powerKick` stays true —
 * "thundersaround that line" reads as bolts kept appearing wherever the
 * ribbon currently is, not a single burst at the strike. Each bolt still
 * bakes its own jagged shape once at spawn (boostThunder.ts's fractal
 * midpoint-displacement, see there for the algorithm) since ITS origin does
 * not move once born — only the pool as a whole is continuous.
 */
const POWER_TRAIL_COLOR = 0xff3a1a;
/** HDR multiplier so the powered trail crosses the postfx bloom threshold —
 *  same trick boostAura.ts's GLOW_INTENSITY uses; see that file for why this
 *  is the correct way to "glow" in this engine, not a shader-drawn fake one.
 *  Baked directly into the uColor uniform (see updateTrail) rather than a
 *  shader change, since the ribbon shader already just outputs uColor as-is. */
const POWER_TRAIL_GLOW_INTENSITY = 2.0;
/** Wider than the ordinary trail's TRAIL_HALF_WIDTH (0.09m) — the reference
 *  ribbon reads as a thick, present line, not a thin scratch. */
const POWER_TRAIL_HALF_WIDTH = 0.15;

const MAX_KICK_BOLTS = 5; // concurrent sparks along the trail — small, quiet
const KICK_BOLT_INTERVAL_S = 0.09; // spawn cadence while state.ball.powerKick is true
const KICK_BOLT_LEVELS = 2; // -> 4 segments per bolt, a quick spark not a full arc
const KICK_BOLT_SEGMENTS = 1 << KICK_BOLT_LEVELS;
const KICK_BOLT_LIFE_S = 0.18;
const KICK_BOLT_REACH_M = 0.26; // metres a spark travels from its spawn point
const KICK_BOLT_JITTER_FRAC = 0.35; // first-level sideways kink, fraction of reach
const KICK_BOLT_CORE_COLOR = 0xfff6ec;
const KICK_BOLT_EDGE_COLOR = 0x8f0000; // same dark red the wave/embers settle to
const KICK_BOLT_GLOW_INTENSITY = 2.2;
const KICK_BOLT_VERTS_PER_BOLT = KICK_BOLT_SEGMENTS * 2;
const KICK_BOLT_MAX_VERTS = MAX_KICK_BOLTS * KICK_BOLT_VERTS_PER_BOLT;

/** Grass spray fires when a moving player's velocity vector snaps hard — a slide
 *  or a sharp cut (§6.7). Derived from (p−prev)/dt and a stored previous velocity. */
const SPRAY_MIN_SPEED = 2.8; // m/s — must actually be moving to kick up turf
const SPRAY_ACCEL_TRIGGER = 26; // m/s² of velocity change → studs digging in
const SPRAY_YAW_TRIGGER = 6.0; // rad/s of body-yaw change → a cut
const SPRAY_COOLDOWN = 0.07; // s between bursts from one player
const SPRAY_PER_BURST = 9;
const SPRAY_LIFE = 0.55; // s
const SPRAY_MAX = 256; // pool cap at Ultra
const SPRAY_DRAG = 3.2; // horizontal air drag (1/s)
/** Default fleck colour — the existing turf-slide green. */
const SPRAY_GRASS_COLOR = 0x5c8a3a;
/** Owner, 2026-08-15: "make the dust fly please" — the kick-impact burst
 *  reuses this exact particle system (position/velocity/gravity/drag all
 *  proven code) with a tan dust colour instead of turf green, so a power
 *  kick throws up its own small cloud at the strike point. */
const DUST_COLOR = 0xcbb896;

const RAIN_MAX = 6000; // drop cap at Ultra
const RAIN_BOX_W = 64; // m, box around the camera the rain fills
const RAIN_BOX_H = 34; // m
const RAIN_FALL_MIN = 22; // m/s
const RAIN_FALL_MAX = 30; // m/s
const RAIN_STREAK = 0.9; // m streak length

/** Angle wrapped to (−π, π]. */
function wrapAngle(a: number): number {
  let x = a;
  while (x > Math.PI) x -= Math.PI * 2;
  while (x < -Math.PI) x += Math.PI * 2;
  return x;
}

/** Lerp two `Color`s into `out` by `t` in [0,1]. Allocation-free per call —
 *  same helper boostAura.ts defines locally; small enough not to share. */
function lerpColorInto(a: Color, b: Color, t: number, out: Color): Color {
  out.r = a.r + (b.r - a.r) * t;
  out.g = a.g + (b.g - a.g) * t;
  out.b = a.b + (b.b - a.b) * t;
  return out;
}

// ---------------------------------------------------------------------------
// Goal net — a verlet cloth "back + roof" sheet, pinned on all four borders.
//
// The sheet drapes from the crossbar (front-top), back along the roof, then
// down the back of the goal to the floor — one continuous grid, because that is
// exactly the surface a ball billows on a broadcast goal. Side panels are cheap
// static triangles built once (their edges match the sheet's pinned borders, so
// the seam never gaps). Only the interior of the sheet moves. (§6.7)
// ---------------------------------------------------------------------------

class GoalNet {
  readonly mesh: Mesh;
  private readonly geom: BufferGeometry;
  /** Live positions — this IS the geometry's position attribute array. */
  private readonly cur: Float32Array;
  /** Verlet previous positions (implicit velocity = cur − prev). */
  private readonly prev: Float32Array;
  /** The designed drape; the target the sheet relaxes back toward. */
  private readonly rest: Float32Array;
  private readonly normal: Float32Array;
  private readonly pinned: Uint8Array;
  /** Structural rest lengths, precomputed so the solver never calls sqrt on rest. */
  private readonly restU: Float32Array; // (COLS-1) × ROWS  along width
  private readonly restV: Float32Array; // COLS × (ROWS-1)  along the drape
  private wasInside = false;

  constructor(
    private readonly sign: number,
    halfLength: number,
    material: MeshBasicMaterial,
  ) {
    const cols = NET_COLS;
    const rows = NET_ROWS;
    const n = cols * rows;
    this.cur = new Float32Array(n * 3);
    this.prev = new Float32Array(n * 3);
    this.rest = new Float32Array(n * 3);
    this.normal = new Float32Array(n * 3);
    this.pinned = new Uint8Array(n);
    this.restU = new Float32Array((cols - 1) * rows);
    this.restV = new Float32Array(cols * (rows - 1));

    const uv = new Float32Array(n * 2);
    const halfW = GOAL_WIDTH * 0.5;
    const backX = sign * (halfLength + GOAL_DEPTH);
    const frontX = sign * halfLength;
    const drapeLen = GOAL_DEPTH + GOAL_HEIGHT;
    // ~12cm net cell for UV tiling of the woven texture.
    const cellM = 0.12;

    for (let row = 0; row < rows; row++) {
      const t = row / (rows - 1);
      const arc = t * drapeLen;
      let x: number;
      let y: number;
      if (arc <= GOAL_DEPTH) {
        // Roof: crossbar back to the top of the rear frame.
        x = frontX + this.sign * arc;
        y = GOAL_HEIGHT;
      } else {
        // Back: straight down the rear frame to the floor.
        x = backX;
        y = GOAL_HEIGHT - (arc - GOAL_DEPTH);
      }
      for (let col = 0; col < cols; col++) {
        const cf = col / (cols - 1);
        const z = -halfW + cf * GOAL_WIDTH;
        const c = row * cols + col;
        const o = c * 3;
        this.rest[o] = x;
        this.rest[o + 1] = y;
        this.rest[o + 2] = z;
        this.cur[o] = x;
        this.cur[o + 1] = y;
        this.cur[o + 2] = z;
        this.prev[o] = x;
        this.prev[o + 1] = y;
        this.prev[o + 2] = z;
        uv[c * 2] = (cf * GOAL_WIDTH) / cellM;
        uv[c * 2 + 1] = arc / cellM;
        // Pin the whole border: front row (crossbar), back-floor row, and both
        // side columns are all lashed to the frame / staked to the ground.
        if (row === 0 || row === rows - 1 || col === 0 || col === cols - 1) {
          this.pinned[c] = 1;
        }
      }
    }

    // Precompute structural rest lengths from the (fixed) rest positions.
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols - 1; col++) {
        const a = (row * cols + col) * 3;
        const b = (row * cols + col + 1) * 3;
        this.restU[row * (cols - 1) + col] = Math.hypot(
          this.rest[a]! - this.rest[b]!,
          this.rest[a + 1]! - this.rest[b + 1]!,
          this.rest[a + 2]! - this.rest[b + 2]!,
        );
      }
    }
    for (let row = 0; row < rows - 1; row++) {
      for (let col = 0; col < cols; col++) {
        const a = (row * cols + col) * 3;
        const b = ((row + 1) * cols + col) * 3;
        this.restV[row * cols + col] = Math.hypot(
          this.rest[a]! - this.rest[b]!,
          this.rest[a + 1]! - this.rest[b + 1]!,
          this.rest[a + 2]! - this.rest[b + 2]!,
        );
      }
    }

    // Triangle index over the grid, built once.
    const idx = new Uint16Array((cols - 1) * (rows - 1) * 6);
    let k = 0;
    for (let row = 0; row < rows - 1; row++) {
      for (let col = 0; col < cols - 1; col++) {
        const a = row * cols + col;
        const b = a + 1;
        const cc = a + cols;
        const d = cc + 1;
        idx[k++] = a; idx[k++] = cc; idx[k++] = b;
        idx[k++] = b; idx[k++] = cc; idx[k++] = d;
      }
    }

    this.geom = new BufferGeometry();
    const posAttr = new BufferAttribute(this.cur, 3);
    posAttr.setUsage(DynamicDrawUsage);
    const normAttr = new BufferAttribute(this.normal, 3);
    normAttr.setUsage(DynamicDrawUsage);
    this.geom.setAttribute('position', posAttr);
    this.geom.setAttribute('normal', normAttr);
    this.geom.setAttribute('uv', new BufferAttribute(uv, 2));
    this.geom.setIndex(new BufferAttribute(idx, 1));
    this.computeNormals();
    this.mesh = new Mesh(this.geom, material);
    this.mesh.name = `goal-net-${sign > 0 ? 'pos' : 'neg'}`;
    // The net is a big, thin, mostly-empty surface; skip frustum culling churn.
    this.mesh.frustumCulled = false;
  }

  /** Smooth per-vertex normals from central grid differences — zero-alloc. */
  private computeNormals(): void {
    const cols = NET_COLS;
    const rows = NET_ROWS;
    const p = this.cur;
    const nrm = this.normal;
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const c = row * cols + col;
        const l = (col > 0 ? c - 1 : c) * 3;
        const r = (col < cols - 1 ? c + 1 : c) * 3;
        const u = (row > 0 ? c - cols : c) * 3;
        const d = (row < rows - 1 ? c + cols : c) * 3;
        const tux = p[r]! - p[l]!;
        const tuy = p[r + 1]! - p[l + 1]!;
        const tuz = p[r + 2]! - p[l + 2]!;
        const tvx = p[d]! - p[u]!;
        const tvy = p[d + 1]! - p[u + 1]!;
        const tvz = p[d + 2]! - p[u + 2]!;
        let nx = tvy * tuz - tvz * tuy;
        let ny = tvz * tux - tvx * tuz;
        let nz = tvx * tuy - tvy * tux;
        const len = Math.hypot(nx, ny, nz) || 1;
        nx /= len; ny /= len; nz /= len;
        const o = c * 3;
        nrm[o] = nx; nrm[o + 1] = ny; nrm[o + 2] = nz;
      }
    }
  }

  /**
   * Advance the cloth. `bx,by,bz` is the ball; `bspeed` its speed. Substeps and
   * dt are bounded so a big frame dt (alt-tab) cannot detonate the solver.
   */
  step(
    dt: number,
    substeps: number,
    elapsed: number,
    bx: number,
    by: number,
    bz: number,
    bvx: number,
    bvz: number,
    bspeed: number,
  ): void {
    const simDt = Math.min(dt, 1 / 30);
    const h = simDt / substeps;
    const h2 = h * h;
    // Subtle wind so the net is never dead-still, sold off the wall clock.
    const windX = Math.sin(elapsed * 1.7) * 0.4;
    const windZ = Math.cos(elapsed * 1.3) * 0.3;

    const halfLnodeX = this.sign; // outward = +sign in x
    const halfW = GOAL_WIDTH * 0.5;
    // Is the ball inside this goal's volume (past the line, within the frame)?
    const sx = this.sign * bx;
    const lineX = this.sign * (this.rest[0]! * this.sign); // = |front x|
    // rest[0].x is the front-top-left node x = sign*halfLength.
    const frontMag = Math.abs(this.rest[0]!);
    void lineX;
    const inside =
      sx > frontMag - BALL_RADIUS &&
      sx < frontMag + GOAL_DEPTH + 0.4 &&
      Math.abs(bz) < halfW + 0.3 &&
      by < GOAL_HEIGHT + 0.3 &&
      by > -0.2;

    if (inside) {
      const bulge = Math.min(NET_BULGE_MAX, NET_BULGE_BASE + bspeed * NET_BULGE_PER_SPEED);
      const entry = !this.wasInside;
      const impulse = entry ? NET_ENTRY_IMPULSE * Math.min(bspeed / SHOT_MAX_SPEED, 1) : 0;
      const rInv = 1 / NET_BALL_RADIUS;
      const n = NET_COLS * NET_ROWS;
      for (let c = 0; c < n; c++) {
        if (this.pinned[c] === 1) continue;
        const o = c * 3;
        // Distance from ball to this node's REST position (stable influence zone).
        const dx = this.rest[o]! - bx;
        const dy = this.rest[o + 1]! - by;
        const dz = this.rest[o + 2]! - bz;
        const d = Math.hypot(dx, dy, dz);
        if (d >= NET_BALL_RADIUS) continue;
        const w = 1 - d * rInv; // 1 at the ball, 0 at the edge of influence
        // Bulge target: rest, displaced outward (+sign x) and nudged toward the
        // ball in y/z so the pocket forms around where it actually struck.
        const targetX = this.rest[o]! + halfLnodeX * bulge * w;
        const targetY = this.rest[o + 1]! + (by - this.rest[o + 1]!) * 0.5 * w;
        const targetZ = this.rest[o + 2]! + (bz - this.rest[o + 2]!) * 0.5 * w;
        this.cur[o] = this.cur[o]! + (targetX - this.cur[o]!) * NET_PUSH_LERP;
        this.cur[o + 1] = this.cur[o + 1]! + (targetY - this.cur[o + 1]!) * NET_PUSH_LERP;
        this.cur[o + 2] = this.cur[o + 2]! + (targetZ - this.cur[o + 2]!) * NET_PUSH_LERP;
        if (impulse > 0) {
          // One-shot snap: move cur without touching prev → outward velocity,
          // biased along the ball's horizontal travel for a directional punch.
          // ⚠ THE TWO LATERAL BIAS TERMS BELOW ARE HARD-CLAMPED. They used to
          // scale directly off raw bvx/bvz with no cap at all — at a fast (or,
          // since the boost-2 power kick, boosted past SHOT_MAX_SPEED) strike
          // this could move a node further in one frame than the distance to
          // its neighbours (~0.32-0.39m grid spacing), which the verlet
          // solver cannot recover from: satisfy() only preserves edge LENGTH,
          // not fold direction, so the patch inverts/tangles and STAYS that
          // way — the net only rebuilds at kickoff (onPitchConfig), never
          // after a goal, so one hard shot could leave a goal's net visibly
          // broken (a stray strand curving off the frame) for the rest of the
          // match. Capped to the same order of magnitude as NET_ENTRY_IMPULSE
          // itself, which was ALREADY correctly bounded this whole time.
          const lateralCap = NET_ENTRY_IMPULSE * 0.6;
          this.cur[o] = this.cur[o]! + halfLnodeX * impulse * w;
          this.cur[o + 2] = this.cur[o + 2]! + Math.max(-lateralCap, Math.min(lateralCap, bvz * 0.01)) * w;
          const xBias = (this.sign > 0 ? Math.abs(bvx) : -Math.abs(bvx)) * 0.008;
          this.cur[o] = this.cur[o]! + Math.max(-lateralCap, Math.min(lateralCap, xBias)) * w;
        }
      }
    }
    this.wasInside = inside;

    for (let s = 0; s < substeps; s++) {
      this.integrate(h2, windX, windZ);
      for (let it = 0; it < NET_ITERATIONS; it++) this.relax();
      this.applyPins();
    }
    this.computeNormals();
    (this.geom.getAttribute('position') as BufferAttribute).needsUpdate = true;
    (this.geom.getAttribute('normal') as BufferAttribute).needsUpdate = true;
  }

  private integrate(h2: number, windX: number, windZ: number): void {
    const n = NET_COLS * NET_ROWS;
    const cur = this.cur;
    const prev = this.prev;
    const rest = this.rest;
    for (let c = 0; c < n; c++) {
      if (this.pinned[c] === 1) continue;
      const o = c * 3;
      for (let a = 0; a < 3; a++) {
        const i = o + a;
        const x = cur[i]!;
        // Verlet velocity term.
        let nx = x + (x - prev[i]!) * NET_DAMPING;
        // Accelerations: gravity (y) + a little wind (x,z).
        if (a === 1) nx += NET_GRAVITY * h2;
        else if (a === 0) nx += windX * h2;
        else nx += windZ * h2;
        // Weak shape memory pulls the sheet back to its designed drape.
        nx += (rest[i]! - x) * NET_SHAPE_MEMORY;
        prev[i] = x;
        cur[i] = nx;
      }
    }
  }

  private relax(): void {
    const cols = NET_COLS;
    const rows = NET_ROWS;
    const cur = this.cur;
    // Width springs.
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols - 1; col++) {
        this.satisfy((row * cols + col), (row * cols + col + 1), this.restU[row * (cols - 1) + col]!);
      }
    }
    // Drape springs.
    for (let row = 0; row < rows - 1; row++) {
      for (let col = 0; col < cols; col++) {
        this.satisfy((row * cols + col), ((row + 1) * cols + col), this.restV[row * cols + col]!);
      }
    }
    void cur;
  }

  /** Distance constraint between two nodes; pinned endpoints do not move. */
  private satisfy(ca: number, cb: number, restLen: number): void {
    const a = ca * 3;
    const b = cb * 3;
    const cur = this.cur;
    const dx = cur[b]! - cur[a]!;
    const dy = cur[b + 1]! - cur[a + 1]!;
    const dz = cur[b + 2]! - cur[a + 2]!;
    const d = Math.hypot(dx, dy, dz);
    if (d < 1e-6) return;
    const diff = (d - restLen) / d;
    const pa = this.pinned[ca] === 1;
    const pb = this.pinned[cb] === 1;
    // Split the correction by which ends are free.
    const wa = pa ? 0 : pb ? 1 : 0.5;
    const wb = pb ? 0 : pa ? 1 : 0.5;
    cur[a] = cur[a]! + dx * diff * wa;
    cur[a + 1] = cur[a + 1]! + dy * diff * wa;
    cur[a + 2] = cur[a + 2]! + dz * diff * wa;
    cur[b] = cur[b]! - dx * diff * wb;
    cur[b + 1] = cur[b + 1]! - dy * diff * wb;
    cur[b + 2] = cur[b + 2]! - dz * diff * wb;
  }

  private applyPins(): void {
    const n = NET_COLS * NET_ROWS;
    for (let c = 0; c < n; c++) {
      if (this.pinned[c] !== 1) continue;
      const o = c * 3;
      this.cur[o] = this.rest[o]!;
      this.cur[o + 1] = this.rest[o + 1]!;
      this.cur[o + 2] = this.rest[o + 2]!;
      this.prev[o] = this.rest[o]!;
      this.prev[o + 1] = this.rest[o + 1]!;
      this.prev[o + 2] = this.rest[o + 2]!;
    }
  }

  dispose(): void {
    this.geom.dispose();
  }
}

// ---------------------------------------------------------------------------
// VFX subsystem
// ---------------------------------------------------------------------------

class VfxSystem implements RenderSubsystem {
  readonly name = 'vfx';

  private ctx!: RenderContext;

  /**
   * False when the STADIUM supplies the goal frames (the V5 bowl models its
   * own, and its `stadium_goals.glb` lands on exactly the same posts — measured
   * at halfLength + GOAL_DEPTH). Only the FRAME is suppressed: the verlet NET
   * below stays, because it billows off the ball and the modelled net is static
   * alpha panels. Two frames on the same posts z-fight along every bar.
   */

  constructor(opts: { drawGoalFrames?: boolean } = {}) {
    // `drawGoalFrames` is accepted for API compatibility and IGNORED since
    // 2026-08-16: the goal is always vfx's now (see the net comment below),
    // so there is no longer a mode in which these are not drawn.
    void opts.drawGoalFrames;
  }

  // ── Nets ──────────────────────────────────────────────────────────────
  private nets: GoalNet[] = [];
  private netMaterial!: MeshBasicMaterial;
  private netTexture!: Texture;
  private sidePanels: Mesh | null = null;
  /** Physical white posts/crossbars. The net alone is too easy to lose at a
   * broadcast distance, and made the playable target visually ambiguous. */
  private goalFrames: Group[] = [];
  private netSubsteps = 3;

  // ── Ball trail ────────────────────────────────────────────────────────
  private trailMesh!: Mesh;
  private trailGeom!: BufferGeometry;
  private trailVerts!: Float32Array; // (MAX_SAMPLES*2) × 3
  private trailAlpha!: Float32Array; // (MAX_SAMPLES*2)
  private readonly trailSamples = new Float32Array(TRAIL_MAX_SAMPLES * 3);
  private trailLen = 0;
  private trailGlobal = 0; // 0..1 master fade
  private trailCap = TRAIL_MAX_SAMPLES;

  /** Last frame's `state.ball.powerKick` — the rising edge (false -> true) is
   *  the one-shot dust-burst trigger; see `updateKickBurst`. */
  private wasPowerKick = false;

  // ── Kick-impact lightning: a pool of independent one-shot sparks, spawned
  // continuously while state.ball.powerKick is true (see KICK_BOLT_* header) ─
  private kickBoltLine!: LineSegments;
  private kickBoltGeom!: BufferGeometry;
  private kickBoltPosAttr!: BufferAttribute;
  private kickBoltColorAttr!: BufferAttribute;
  private kickBoltAlphaAttr!: BufferAttribute;
  /** Age/life per pooled bolt slot; life <= 0 = free. Each slot's shape is
   *  baked once at spawn into its fixed vertex range (slot i owns vertices
   *  [i*KICK_BOLT_VERTS_PER_BOLT, (i+1)*KICK_BOLT_VERTS_PER_BOLT)) — only the
   *  alpha envelope is touched afterward, same "static origin" reasoning as
   *  the file header's note on why this differs from boostThunder.ts. */
  private readonly kickBoltAge = new Float32Array(MAX_KICK_BOLTS);
  private readonly kickBoltLife = new Float32Array(MAX_KICK_BOLTS);
  private kickBoltCursor = 0;
  private kickBoltSpawnTimer = 0;

  // ── Grass spray ───────────────────────────────────────────────────────
  private sprayPoints!: Points;
  private sprayGeom!: BufferGeometry;
  private sprayPos!: Float32Array; // SPRAY_MAX × 3
  private sprayVel!: Float32Array; // SPRAY_MAX × 3
  private sprayColor!: Float32Array; // SPRAY_MAX × 3
  private readonly sprayLife = new Float32Array(SPRAY_MAX);
  private readonly sprayLifeAttr = new Float32Array(SPRAY_MAX);
  private sprayCursor = 0;
  private sprayCap = SPRAY_MAX;
  // Per-player derivatives the seam does not carry (we store what we need).
  private readonly pVelX = new Float32Array(MAX_PLAYERS);
  private readonly pVelZ = new Float32Array(MAX_PLAYERS);
  private readonly pYaw = new Float32Array(MAX_PLAYERS);
  private readonly pSeen = new Uint8Array(MAX_PLAYERS);
  private readonly pCooldown = new Float32Array(MAX_PLAYERS);

  // ── Rain ──────────────────────────────────────────────────────────────
  private rainLines!: LineSegments;
  private rainGeom!: BufferGeometry;
  private rainVerts!: Float32Array; // RAIN_MAX*2 × 3
  private rainPos!: Float32Array; // RAIN_MAX × 3 (the falling drop head)
  private readonly rainFall = new Float32Array(RAIN_MAX);
  private rainCap = RAIN_MAX;
  private rainSeeded = false;
  private wet = false;

  // ── Screen-space wetness cue (camera-facing quad) ─────────────────────
  private wetQuad!: Mesh;
  private wetTexture!: Texture;
  private wetActive = false;

  // ── Scratch (zero-alloc) ──────────────────────────────────────────────
  private readonly camPos = new Vector3();
  private readonly camFwd = new Vector3();
  private readonly camQuat = new Quaternion();
  private readonly sDir = new Vector3();
  private readonly sSide = new Vector3();
  private readonly sTmp = new Vector3();
  private readonly scratchColor = new Color();
  private readonly powerTrailColor = new Color(POWER_TRAIL_COLOR).multiplyScalar(POWER_TRAIL_GLOW_INTENSITY);
  private readonly normalTrailColor = new Color(0xbfe6ff);
  private readonly kickBoltCore = new Color(KICK_BOLT_CORE_COLOR);
  private readonly kickBoltEdge = new Color(KICK_BOLT_EDGE_COLOR);
  private readonly dustColor = new Color(DUST_COLOR);
  private readonly sprayGrassColor = new Color(SPRAY_GRASS_COLOR);

  init(ctx: RenderContext): void {
    this.ctx = ctx;
    this.applyTierCaps(ctx.quality);

    // Net material + woven texture (shared by both goals' sheets and the sides).
    this.netTexture = makeNetTexture();
    // ⚠ UNLIT, ON PURPOSE (owner, 2026-08-16, screenshot annotated "Net"). A
    // LIT net is a dark grey slab from the front: the sheet faces INTO the
    // goal, so the sun is behind it and Lambert shades every thread toward
    // black — on a broadcast camera that reads as a filled panel hanging in
    // the frame, not as netting. Real goal netting is thin bright cord that
    // scatters light from every side; unlit white is the honest match, and it
    // is what keeps the weave legible at 40 m where shading detail is gone
    // anyway.
    this.netMaterial = new MeshBasicMaterial({
      map: this.netTexture,
      color: 0xf4f8fb,
      side: DoubleSide,
      // Cutout, not blended: the weave holes are hard alpha, so the net writes
      // depth and renders in the opaque pass — no transparency sort, plays nice
      // with SSAO/shadows from the lighting rig. (§6.1)
      transparent: false,
      alphaTest: 0.28, // 0.5 killed mipped threads at distance — see makeNetTexture
    });

    this.buildTrail(ctx);
    this.buildKickLightning(ctx);
    this.buildGrass(ctx);
    this.buildRain(ctx);
    this.buildWetCue(ctx);
  }

  onPitchConfig(config: MatchConfig, ctx: RenderContext): void {
    // Tear down any nets from a previous match config (§9.2 rebuild).
    for (const net of this.nets) {
      ctx.scene.remove(net.mesh);
      net.dispose();
    }
    this.nets.length = 0;
    if (this.sidePanels) {
      ctx.scene.remove(this.sidePanels);
      this.sidePanels.geometry.dispose();
      this.sidePanels = null;
    }
    for (const frame of this.goalFrames) {
      ctx.scene.remove(frame);
      disposeGoalFrame(frame);
    }
    this.goalFrames.length = 0;

    const halfLength = config.pitchLength * 0.5;
    // The stadium bowl's modelled goals only exist at the FULL pitch's goal
    // line (±52.5 + depth). Any smaller preset (§9.2) puts the sim's goals
    // inboard of them, so the frame must come from here regardless of
    // drawGoalFrames — otherwise the net billows around an invisible frame
    // while the bowl's white goal stands offside in the distance (owner,
    // 2026-08-15: "net position is off"). StadiumV5System hides its goals
    // module for these pitches; the two checks use the same 104m threshold.
    // A goal at each end of the pitch (§6.7: "goals are at ±pitchLength/2").
    for (const sign of [-1, 1]) {
      const net = new GoalNet(sign, halfLength, this.netMaterial);
      // ⚠ THE NET FOLLOWS THE FRAME'S RULE (owner, 2026-08-16, screenshot of a
      // doubled goal at kickoff). The frame was already hidden when the
      // stadium models its own goals, but the NET was added unconditionally -
      // so on a full-size pitch the flagship's `V5_goals`/`MERGED_goals` and
      // this net were both drawn, one inside the other, and the goal read as a
      // ghosted double. Measured in the live scene: V5_goals vis=true,
      // MERGED_goals vis=true, goal-net-neg/pos vis=true, goal-frame-* false.
      // Always visible: the vfx net is built AT THE SIM'S GOAL LINE, so it is
      // right at any pitch size and identical in every mode (owner: "make sure
      // it look same in all modes"). Hiding it on full-size pitches - my
      // attempt at the doubled goal - left the bowl's bare frame with NO NET,
      // which is what the owner then photographed. The duplicate is solved at
      // the other end instead: StadiumV5System no longer mounts its goals.
      net.mesh.visible = true;
      this.nets.push(net);
      ctx.scene.add(net.mesh);
      const frame = buildGoalFrame(sign, halfLength);
      frame.visible = true;
      this.goalFrames.push(frame);
      ctx.scene.add(frame);
    }
    this.sidePanels = buildSidePanels(halfLength, this.netMaterial);
    this.sidePanels.visible = true;
    ctx.scene.add(this.sidePanels);

    // Wet-pitch rain is a match setting (§3.2); (re)seed the field on config.
    this.wet = config.wetPitch;
    this.rainLines.visible = this.wet && this.rainCap > 0;
    if (this.wet && !this.rainSeeded) {
      this.seedRain(ctx);
      this.rainSeeded = true;
    }
  }

  update(state: RenderState, ctx: RenderContext): void {
    const dt = state.dt;
    // Camera basis for billboarding — read the transform the shell set THIS
    // frame (applyCamera runs before render), not the possibly-stale matrixWorld.
    this.camPos.copy(ctx.camera.position);
    this.camQuat.copy(ctx.camera.quaternion);
    this.camFwd.set(0, 0, -1).applyQuaternion(this.camQuat);

    this.updateNets(state);
    this.updateTrail(state, dt);
    this.updateKickBurst(state, dt);
    this.updateGrass(state, dt);
    this.updateRain(dt, state.elapsed);
    this.updateWetCue(ctx, state.elapsed);
  }

  setQuality(quality: QualitySettings, ctx: RenderContext): void {
    this.applyTierCaps(quality);
    // Nothing reallocates — buffers are Ultra-sized and we only change how much
    // of each we simulate/draw. Just re-evaluate visibility for the new caps.
    this.rainLines.visible = this.wet && this.rainCap > 0;
    void ctx;
  }

  dispose(): void {
    for (const net of this.nets) net.dispose();
    this.nets.length = 0;
    for (const frame of this.goalFrames) disposeGoalFrame(frame);
    this.goalFrames.length = 0;
    this.sidePanels?.geometry.dispose();
    this.netMaterial.dispose();
    this.netTexture.dispose();
    this.trailGeom.dispose();
    (this.trailMesh.material as ShaderMaterial).dispose();
    this.kickBoltGeom.dispose();
    (this.kickBoltLine.material as ShaderMaterial).dispose();
    this.sprayGeom.dispose();
    (this.sprayPoints.material as ShaderMaterial).dispose();
    this.rainGeom.dispose();
    (this.rainLines.material as LineBasicMaterial).dispose();
    this.wetQuad.geometry.dispose();
    (this.wetQuad.material as ShaderMaterial).dispose();
    this.wetTexture.dispose();
  }

  // ── Tier scaling (§6.8) ────────────────────────────────────────────────
  private applyTierCaps(quality: QualitySettings): void {
    const rank = QUALITY_RANK[quality.tier];
    // low / medium / high / ultra
    this.netSubsteps = rank <= 0 ? 2 : rank === 1 ? 3 : 4;
    this.sprayCap = rank <= 0 ? 48 : rank === 1 ? 96 : rank === 2 ? 176 : SPRAY_MAX;
    this.trailCap = rank <= 0 ? 12 : rank === 1 ? 18 : rank === 2 ? 26 : TRAIL_MAX_SAMPLES;
    // Rain streaks are one cheap LineSegments draw, but the Intel-iGPU floor
    // keeps the drop count modest at Low. (§6.8)
    this.rainCap = rank <= 0 ? 1200 : rank === 1 ? 2800 : rank === 2 ? 4200 : RAIN_MAX;
    // The screen-space wetness cue is a post-y flourish — off below Medium so
    // Low stays clean and fast on integrated graphics.
    this.wetActive = rank >= 1;
  }

  // ── Nets ────────────────────────────────────────────────────────────────
  private updateNets(state: RenderState): void {
    if (this.nets.length === 0) return;
    const b = state.ball;
    // Ball horizontal velocity from the shell's prev-frame ball position.
    const bvx = (b.px - state.ballPrev.px) / state.dt;
    const bvz = (b.pz - state.ballPrev.pz) / state.dt;
    for (const net of this.nets) {
      net.step(state.dt, this.netSubsteps, state.elapsed, b.px, b.py, b.pz, bvx, bvz, b.speed);
    }
  }

  // ── Ball trail (§6.7 — subtle ribbon, fast shots ONLY) ─────────────────
  private buildTrail(ctx: RenderContext): void {
    const verts = TRAIL_MAX_SAMPLES * 2;
    this.trailVerts = new Float32Array(verts * 3);
    this.trailAlpha = new Float32Array(verts);
    const idx = new Uint16Array((TRAIL_MAX_SAMPLES - 1) * 6);
    let k = 0;
    for (let s = 0; s < TRAIL_MAX_SAMPLES - 1; s++) {
      const a = s * 2;
      idx[k++] = a; idx[k++] = a + 1; idx[k++] = a + 2;
      idx[k++] = a + 1; idx[k++] = a + 3; idx[k++] = a + 2;
    }
    this.trailGeom = new BufferGeometry();
    const pa = new BufferAttribute(this.trailVerts, 3);
    pa.setUsage(DynamicDrawUsage);
    const aa = new BufferAttribute(this.trailAlpha, 1);
    aa.setUsage(DynamicDrawUsage);
    this.trailGeom.setAttribute('position', pa);
    this.trailGeom.setAttribute('aAlpha', aa);
    this.trailGeom.setIndex(new BufferAttribute(idx, 1));
    this.trailGeom.setDrawRange(0, 0);
    const mat = new ShaderMaterial({
      uniforms: { uColor: { value: new Color(0xbfe6ff) } },
      vertexShader: /* glsl */ `
        attribute float aAlpha;
        varying float vAlpha;
        void main() {
          vAlpha = aAlpha;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }`,
      fragmentShader: /* glsl */ `
        uniform vec3 uColor;
        varying float vAlpha;
        void main() { gl_FragColor = vec4(uColor, vAlpha); }`,
      transparent: true,
      depthWrite: false,
      blending: AdditiveBlending,
    });
    this.trailMesh = new Mesh(this.trailGeom, mat);
    this.trailMesh.frustumCulled = false;
    this.trailMesh.visible = false;
    this.trailMesh.renderOrder = 5;
    ctx.scene.add(this.trailMesh);
  }

  private updateTrail(state: RenderState, dt: number): void {
    const b = state.ball;
    // A power kick (boost-2 or full-charge) uses a much lower floor than the
    // ordinary fast-shot threshold — "show the trial like aline" for the
    // WHOLE flight, not only above TRAIL_SPEED_ON — but it is NOT
    // unconditional: `powerKick` only changes on the ball's NEXT strike, so a
    // receiving player who traps and dribbles it (no new strike at all) would
    // otherwise leave the red line showing forever. POWER_EFFECT_MIN_SPEED_M_S
    // is what makes it stop the instant the ball is actually done moving
    // ("once the animation is over its done" — owner, 2026-08-15). It also
    // recolours the same ribbon red (see the material update below); this is
    // intentionally the SAME mesh as the pale everyday trail, not a second
    // one, so the two can never show two disagreeing lines behind one ball.
    const fast = b.speed > TRAIL_SPEED_ON || (b.powerKick && b.speed > POWER_EFFECT_MIN_SPEED_M_S);
    const slow = b.speed < TRAIL_SPEED_OFF;

    if (fast) {
      // Append a sample when the ball has moved far enough from the last one.
      let add = this.trailLen === 0;
      if (!add) {
        const last = (this.trailLen - 1) * 3;
        const dx = b.px - this.trailSamples[last]!;
        const dy = b.py - this.trailSamples[last + 1]!;
        const dz = b.pz - this.trailSamples[last + 2]!;
        add = dx * dx + dy * dy + dz * dz > TRAIL_MIN_SPACING * TRAIL_MIN_SPACING;
      }
      if (add) {
        if (this.trailLen >= this.trailCap) {
          // Drop the oldest sample (in-place shift, zero-alloc).
          this.trailSamples.copyWithin(0, 3, this.trailLen * 3);
          this.trailLen--;
        }
        const o = this.trailLen * 3;
        this.trailSamples[o] = b.px;
        this.trailSamples[o + 1] = b.py;
        this.trailSamples[o + 2] = b.pz;
        this.trailLen++;
      }
      this.trailGlobal = Math.min(1, this.trailGlobal + TRAIL_FADE_UP * dt);
    } else {
      this.trailGlobal = Math.max(0, this.trailGlobal - TRAIL_FADE_DOWN * dt);
      if (slow && this.trailGlobal <= 0.02) {
        this.trailLen = 0;
        this.trailGlobal = 0;
      }
    }

    if (this.trailLen < 2 || this.trailGlobal <= 0.02) {
      this.trailMesh.visible = false;
      return;
    }

    // Recolour + rebrighten the SAME ribbon for a power kick — vivid red,
    // HDR-boosted so bloom picks it up (see POWER_TRAIL_GLOW_INTENSITY),
    // NormalBlending instead of the pale trail's AdditiveBlending (additive
    // red at meaningful alpha washes to cream under this game's ACES
    // tonemap — the exact lesson boostAura.ts's header already documents at
    // length; re-applying AdditiveBlending here would reproduce that bug).
    const mat = this.trailMesh.material as ShaderMaterial;
    mat.blending = b.powerKick ? NormalBlending : AdditiveBlending;
    (mat.uniforms.uColor!.value as Color).copy(b.powerKick ? this.powerTrailColor : this.normalTrailColor);
    const halfWidth = b.powerKick ? POWER_TRAIL_HALF_WIDTH : TRAIL_HALF_WIDTH;

    // Build the ribbon: each sample → two verts offset along the camera-facing
    // side vector; width tapers from head (newest) to tail, alpha fades to tail.
    const n = this.trailLen;
    for (let i = 0; i < n; i++) {
      const o = i * 3;
      // Direction along the trail at this sample.
      const pi = Math.max(0, i - 1) * 3;
      const ni = Math.min(n - 1, i + 1) * 3;
      this.sDir.set(
        this.trailSamples[ni]! - this.trailSamples[pi]!,
        this.trailSamples[ni + 1]! - this.trailSamples[pi + 1]!,
        this.trailSamples[ni + 2]! - this.trailSamples[pi + 2]!,
      );
      if (this.sDir.lengthSq() < 1e-8) this.sDir.set(1, 0, 0);
      this.sDir.normalize();
      // View vector from this point to the camera.
      this.sTmp.set(
        this.camPos.x - this.trailSamples[o]!,
        this.camPos.y - this.trailSamples[o + 1]!,
        this.camPos.z - this.trailSamples[o + 2]!,
      ).normalize();
      this.sSide.crossVectors(this.sDir, this.sTmp);
      if (this.sSide.lengthSq() < 1e-8) this.sSide.set(0, 0, 1);
      this.sSide.normalize();
      const head = i / (n - 1); // 0 tail .. 1 head
      const w = halfWidth * head;
      const a = head * this.trailGlobal * 0.8;
      const vo = i * 2 * 3;
      const cx = this.trailSamples[o]!;
      const cy = this.trailSamples[o + 1]!;
      const cz = this.trailSamples[o + 2]!;
      this.trailVerts[vo] = cx + this.sSide.x * w;
      this.trailVerts[vo + 1] = cy + this.sSide.y * w;
      this.trailVerts[vo + 2] = cz + this.sSide.z * w;
      this.trailVerts[vo + 3] = cx - this.sSide.x * w;
      this.trailVerts[vo + 4] = cy - this.sSide.y * w;
      this.trailVerts[vo + 5] = cz - this.sSide.z * w;
      this.trailAlpha[i * 2] = a;
      this.trailAlpha[i * 2 + 1] = a;
    }
    this.trailGeom.setDrawRange(0, (n - 1) * 6);
    (this.trailGeom.getAttribute('position') as BufferAttribute).needsUpdate = true;
    (this.trailGeom.getAttribute('aAlpha') as BufferAttribute).needsUpdate = true;
    this.trailMesh.visible = true;
  }

  // ── Kick-impact lightning (see the header above TRAIL_FADE_DOWN) ───────
  private buildKickLightning(ctx: RenderContext): void {
    const geo = new BufferGeometry();
    const position = new Float32Array(KICK_BOLT_MAX_VERTS * 3);
    const color = new Float32Array(KICK_BOLT_MAX_VERTS * 3);
    const alpha = new Float32Array(KICK_BOLT_MAX_VERTS);
    this.kickBoltPosAttr = new BufferAttribute(position, 3);
    this.kickBoltColorAttr = new BufferAttribute(color, 3);
    this.kickBoltAlphaAttr = new BufferAttribute(alpha, 1);
    this.kickBoltPosAttr.setUsage(DynamicDrawUsage);
    this.kickBoltColorAttr.setUsage(DynamicDrawUsage);
    this.kickBoltAlphaAttr.setUsage(DynamicDrawUsage);
    geo.setAttribute('position', this.kickBoltPosAttr);
    geo.setAttribute('aColor', this.kickBoltColorAttr);
    geo.setAttribute('aAlpha', this.kickBoltAlphaAttr);
    geo.setDrawRange(0, 0);
    this.kickBoltGeom = geo;

    const mat = new ShaderMaterial({
      vertexShader: /* glsl */ `
        attribute vec3 aColor;
        attribute float aAlpha;
        varying vec3 vColor;
        varying float vAlpha;
        void main() {
          vColor = aColor;
          vAlpha = aAlpha;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }`,
      fragmentShader: /* glsl */ `
        varying vec3 vColor;
        varying float vAlpha;
        void main() {
          if (vAlpha <= 0.003) discard;
          gl_FragColor = vec4(vColor, vAlpha);
        }`,
      transparent: true,
      depthWrite: false,
      depthTest: false, // a strike-point spark, same reasoning as boostThunder.ts's bolts
      blending: NormalBlending,
    });
    const ls = new LineSegments(geo, mat);
    ls.name = 'kick-impact-lightning';
    ls.frustumCulled = false;
    this.kickBoltLine = ls;
    ctx.scene.add(ls);
  }

  /**
   * Bake ONE spark into pool slot `(this.kickBoltCursor % MAX_KICK_BOLTS)`,
   * radiating from `(x, y, z)` — wherever the ball IS at this spawn instant.
   * The shape (fractal midpoint-displacement, boostThunder.ts's algorithm)
   * bakes into that slot's fixed vertex range once, here; `updateKickLightning`
   * only ever touches alpha afterward, since the origin does not move once a
   * given spark is born (the BALL keeps moving, which is why this gets called
   * repeatedly on a timer — each new spark just starts from a newer position).
   */
  private spawnOneKickBolt(x: number, y: number, z: number): void {
    const slot = this.kickBoltCursor % MAX_KICK_BOLTS;
    this.kickBoltCursor++;
    const base = slot * KICK_BOLT_VERTS_PER_BOLT;

    // A random outward direction, then a perpendicular basis to displace the
    // midpoint sideways — same fractal technique as boostThunder.ts's
    // midpointDisplace, inlined here since there is only one segment depth.
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(Math.random() * 2 - 1);
    const dirx = Math.sin(phi) * Math.cos(theta);
    const diry = Math.cos(phi) * 0.6; // biased less vertical — a spark along the ground, not a fountain
    const dirz = Math.sin(phi) * Math.sin(theta);
    const dlen = Math.hypot(dirx, diry, dirz) || 1;
    const ex = x + (dirx / dlen) * KICK_BOLT_REACH_M;
    const ey = Math.max(0.02, y + (diry / dlen) * KICK_BOLT_REACH_M);
    const ez = z + (dirz / dlen) * KICK_BOLT_REACH_M;

    // A simple perpendicular (any consistent basis works — the bolt only
    // needs to kink sideways, not align to a specific up-vector).
    const px = -(diry / dlen), py = (dirx / dlen), pz = 0;
    const plen = Math.hypot(px, py, pz) || 1;
    const bux = px / plen, buy = py / plen, buz = pz / plen;

    let pts: Array<{ t: number; u: number }> = [{ t: 0, u: 0 }, { t: 1, u: 0 }];
    let amp = KICK_BOLT_REACH_M * KICK_BOLT_JITTER_FRAC;
    for (let level = 0; level < KICK_BOLT_LEVELS; level++) {
      const next: typeof pts = [pts[0]!];
      for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i]!, p1 = pts[i + 1]!;
        next.push({ t: (p0.t + p1.t) * 0.5, u: (p0.u + p1.u) * 0.5 + (Math.random() * 2 - 1) * amp });
        next.push(p1);
      }
      pts = next;
      amp *= 0.5;
    }

    // Colour along the bolt's OWN length, baked in now since the shape
    // itself is baked now — bright near the ball, settling to the dark red
    // every other boost-2 effect fades toward, same continuity reasoning as
    // the trail and boostAura.ts's embers.
    let w = base;
    const writePoint = (p: { t: number; u: number }): void => {
      this.kickBoltPosAttr.setXYZ(
        w,
        x + (ex - x) * p.t + bux * p.u, y + (ey - y) * p.t + buy * p.u, z + (ez - z) * p.t + buz * p.u,
      );
      lerpColorInto(this.kickBoltCore, this.kickBoltEdge, p.t, this.scratchColor);
      this.kickBoltColorAttr.setXYZ(
        w,
        this.scratchColor.r * KICK_BOLT_GLOW_INTENSITY,
        this.scratchColor.g * KICK_BOLT_GLOW_INTENSITY,
        this.scratchColor.b * KICK_BOLT_GLOW_INTENSITY,
      );
      w++;
    };
    const end = base + KICK_BOLT_VERTS_PER_BOLT;
    for (let i = 0; i < pts.length - 1 && w + 2 <= end; i++) {
      writePoint(pts[i]!);
      writePoint(pts[i + 1]!);
    }
    this.kickBoltPosAttr.needsUpdate = true;
    this.kickBoltColorAttr.needsUpdate = true;
    this.kickBoltAge[slot] = 0;
    this.kickBoltLife[slot] = KICK_BOLT_LIFE_S;
  }

  private updateKickLightning(dt: number): void {
    let anyAlive = false;
    let maxVert = 0;
    for (let slot = 0; slot < MAX_KICK_BOLTS; slot++) {
      const life = this.kickBoltLife[slot]!;
      const base = slot * KICK_BOLT_VERTS_PER_BOLT;
      if (life <= 0) continue;
      const age = Math.min(this.kickBoltAge[slot]! + dt, life);
      this.kickBoltAge[slot] = age;
      const t = age / life;
      let a: number;
      if (t >= 1) {
        this.kickBoltLife[slot] = 0;
        a = 0;
      } else {
        // Fast attack, fast decay — a spark, not a fade.
        const attack = Math.min(1, t / 0.1);
        const decay = 1 - Math.min(1, Math.max(0, (t - 0.4) / 0.6));
        a = attack * decay;
        anyAlive = true;
        maxVert = base + KICK_BOLT_VERTS_PER_BOLT;
      }
      for (let i = 0; i < KICK_BOLT_VERTS_PER_BOLT; i++) this.kickBoltAlphaAttr.setX(base + i, a);
    }
    this.kickBoltAlphaAttr.needsUpdate = true;
    // Draws through the highest active slot's range rather than compacting —
    // dead slots in between are already alpha-0 and discard in the fragment
    // shader, the same "always draw the pool, let alpha hide the rest" choice
    // boostAura.ts's embers make.
    this.kickBoltGeom.setDrawRange(0, anyAlive ? maxVert : 0);
  }

  /**
   * Continuously spawns kick bolts at the ball's CURRENT position for as
   * long as `state.ball.powerKick` stays true — "thundersaround that line"
   * reads as sparks kept appearing wherever the (also-recoloured) trail
   * currently is, not a single burst at the strike. The DUST, by contrast,
   * stays edge-triggered (the rising edge of `powerKick`) — a kick throws up
   * one cloud of dirt at the moment of contact, not a continuous fountain.
   */
  private updateKickBurst(state: RenderState, dt: number): void {
    const b = state.ball;
    const justKicked = b.powerKick && !this.wasPowerKick;
    this.wasPowerKick = b.powerKick;
    if (justKicked && this.sprayCap > 0) {
      // RenderState.ball carries no vx/vz (only the scalar `speed`) — derive
      // the launch direction from the position delta, same idiom updateNets
      // above already uses for the ball's velocity. Ball velocity IS the
      // strike's own launch direction: dust flings backward relative to it,
      // off the plant foot, the same "opposite to motion" model emitSpray
      // already uses for a player's slide studs.
      const bvx = (b.px - state.ballPrev.px) / dt;
      const bvz = (b.pz - state.ballPrev.pz) / dt;
      this.emitSpray(b.px, 0.02, b.pz, bvx, bvz, Math.hypot(bvx, bvz), this.dustColor);
    }
    // Same POWER_EFFECT_MIN_SPEED_M_S floor the trail uses — sparks stop
    // spawning the instant the ball is actually at rest, not just whenever
    // `powerKick` next changes (see that constant's comment).
    if (b.powerKick && b.speed > POWER_EFFECT_MIN_SPEED_M_S) {
      this.kickBoltSpawnTimer -= dt;
      if (this.kickBoltSpawnTimer <= 0) {
        this.kickBoltSpawnTimer = KICK_BOLT_INTERVAL_S;
        this.spawnOneKickBolt(b.px, Math.max(b.py, BALL_RADIUS), b.pz);
      }
    } else {
      this.kickBoltSpawnTimer = 0;
    }
    this.updateKickLightning(dt);
  }

  // ── Grass spray (§6.7 — turf burst on hard turns/slides) ───────────────
  private buildGrass(ctx: RenderContext): void {
    this.sprayPos = new Float32Array(SPRAY_MAX * 3);
    this.sprayVel = new Float32Array(SPRAY_MAX * 3);
    // Per-particle colour, not a single uniform — owner, 2026-08-15: "make
    // the dust fly please" reuses this exact pool/physics for a kick-impact
    // burst, and a kick's dust needs a different colour (tan) from a slide's
    // turf fleck (green) at the same time, from the same draw call.
    this.sprayColor = new Float32Array(SPRAY_MAX * 3);
    this.sprayGeom = new BufferGeometry();
    const pa = new BufferAttribute(this.sprayPos, 3);
    pa.setUsage(DynamicDrawUsage);
    const la = new BufferAttribute(this.sprayLifeAttr, 1);
    la.setUsage(DynamicDrawUsage);
    const ca = new BufferAttribute(this.sprayColor, 3);
    ca.setUsage(DynamicDrawUsage);
    this.sprayGeom.setAttribute('position', pa);
    this.sprayGeom.setAttribute('aLife', la);
    this.sprayGeom.setAttribute('aColor', ca);
    this.sprayGeom.setDrawRange(0, 0);
    const mat = new ShaderMaterial({
      uniforms: {
        uSize: { value: 46 * Math.min(this.ctx.gl.getPixelRatio(), 2) },
      },
      vertexShader: /* glsl */ `
        attribute float aLife;
        attribute vec3 aColor;
        varying float vLife;
        varying vec3 vColor;
        uniform float uSize;
        void main() {
          vLife = aLife;
          vColor = aColor;
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          // Shrink as the fleck dies; attenuate with distance.
          gl_PointSize = uSize * aLife / max(-mv.z, 1.0);
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: /* glsl */ `
        varying float vLife;
        varying vec3 vColor;
        void main() {
          if (vLife <= 0.0) discard;
          vec2 d = gl_PointCoord - vec2(0.5);
          if (dot(d, d) > 0.25) discard;
          gl_FragColor = vec4(vColor, vLife);
        }`,
      transparent: true,
      depthWrite: false,
      blending: NormalBlending,
    });
    this.sprayPoints = new Points(this.sprayGeom, mat);
    this.sprayPoints.frustumCulled = false;
    this.sprayPoints.renderOrder = 4;
    ctx.scene.add(this.sprayPoints);
  }

  private updateGrass(state: RenderState, dt: number): void {
    const invDt = 1 / dt;
    for (let i = 0; i < MAX_PLAYERS; i++) {
      if (state.active[i] !== 1) continue;
      const vx = (state.px[i]! - state.prevPx[i]!) * invDt;
      const vz = (state.pz[i]! - state.prevPz[i]!) * invDt;
      const yaw = state.yaw[i]!;
      if (this.pSeen[i] === 0) {
        // First sighting: seed derivatives, emit nothing (no garbage spike).
        this.pVelX[i] = vx; this.pVelZ[i] = vz; this.pYaw[i] = yaw; this.pSeen[i] = 1;
        continue;
      }
      const speed = Math.hypot(vx, vz);
      const accel = Math.hypot(vx - this.pVelX[i]!, vz - this.pVelZ[i]!) * invDt;
      const yawRate = Math.abs(wrapAngle(yaw - this.pYaw[i]!)) * invDt;
      this.pVelX[i] = vx; this.pVelZ[i] = vz; this.pYaw[i] = yaw;
      if (this.pCooldown[i]! > 0) this.pCooldown[i] = this.pCooldown[i]! - dt;

      const hardCut = speed > SPRAY_MIN_SPEED &&
        (accel > SPRAY_ACCEL_TRIGGER || yawRate > SPRAY_YAW_TRIGGER);
      if (hardCut && this.pCooldown[i]! <= 0 && this.sprayCap > 0) {
        this.emitSpray(state.px[i]!, state.py[i]!, state.pz[i]!, vx, vz, speed);
        this.pCooldown[i] = SPRAY_COOLDOWN;
      }
    }
    this.integrateSpray(dt);
  }

  private emitSpray(
    fx: number, fy: number, fz: number, vx: number, vz: number, speed: number,
    color: Color = this.sprayGrassColor,
  ): void {
    const dirx = speed > 1e-3 ? -vx / speed : 0;
    const dirz = speed > 1e-3 ? -vz / speed : 0;
    for (let j = 0; j < SPRAY_PER_BURST; j++) {
      const idx = this.sprayCursor % this.sprayCap;
      this.sprayCursor++;
      const o = idx * 3;
      // Emit at the feet with a small random scatter.
      this.sprayPos[o] = fx + (Math.random() - 0.5) * 0.2;
      this.sprayPos[o + 1] = fy + 0.04;
      this.sprayPos[o + 2] = fz + (Math.random() - 0.5) * 0.2;
      // Fling mostly opposite to motion, up and out, with spread.
      const spread = 1.4;
      const back = 1.0 + Math.random() * 1.5;
      this.sprayVel[o] = dirx * back + (Math.random() - 0.5) * spread;
      this.sprayVel[o + 1] = 1.6 + Math.random() * 2.2;
      this.sprayVel[o + 2] = dirz * back + (Math.random() - 0.5) * spread;
      this.sprayLife[idx] = SPRAY_LIFE;
      this.sprayColor[o] = color.r; this.sprayColor[o + 1] = color.g; this.sprayColor[o + 2] = color.b;
    }
    // Colour is set once at emission, never touched again — only dirty it
    // here, not every frame in integrateSpray (nothing there changes it).
    (this.sprayGeom.getAttribute('aColor') as BufferAttribute).needsUpdate = true;
  }

  private integrateSpray(dt: number): void {
    const drag = Math.max(0, 1 - SPRAY_DRAG * dt);
    const cap = this.sprayCap;
    for (let i = 0; i < cap; i++) {
      let life = this.sprayLife[i]!;
      if (life > 0) {
        const o = i * 3;
        life -= dt;
        this.sprayLife[i] = life;
        this.sprayVel[o] = this.sprayVel[o]! * drag;
        this.sprayVel[o + 1] = this.sprayVel[o + 1]! + GRAVITY * dt;
        this.sprayVel[o + 2] = this.sprayVel[o + 2]! * drag;
        this.sprayPos[o] = this.sprayPos[o]! + this.sprayVel[o]! * dt;
        this.sprayPos[o + 1] = this.sprayPos[o + 1]! + this.sprayVel[o + 1]! * dt;
        this.sprayPos[o + 2] = this.sprayPos[o + 2]! + this.sprayVel[o + 2]! * dt;
        if (this.sprayPos[o + 1]! < 0.02) {
          this.sprayPos[o + 1] = 0.02;
          this.sprayLife[i] = 0;
          life = 0;
        }
      }
      this.sprayLifeAttr[i] = life > 0 ? Math.min(1, life / SPRAY_LIFE) : 0;
    }
    this.sprayGeom.setDrawRange(0, cap);
    (this.sprayGeom.getAttribute('position') as BufferAttribute).needsUpdate = true;
    (this.sprayGeom.getAttribute('aLife') as BufferAttribute).needsUpdate = true;
  }

  // ── Rain (§6.7 — instanced streaks + a wetness cue, wet pitch only) ────
  private buildRain(ctx: RenderContext): void {
    this.rainPos = new Float32Array(RAIN_MAX * 3);
    this.rainVerts = new Float32Array(RAIN_MAX * 2 * 3);
    this.rainGeom = new BufferGeometry();
    const pa = new BufferAttribute(this.rainVerts, 3);
    pa.setUsage(DynamicDrawUsage);
    this.rainGeom.setAttribute('position', pa);
    this.rainGeom.setDrawRange(0, 0);
    for (let i = 0; i < RAIN_MAX; i++) {
      this.rainFall[i] = RAIN_FALL_MIN + Math.random() * (RAIN_FALL_MAX - RAIN_FALL_MIN);
    }
    const mat = new LineBasicMaterial({
      color: 0xafc4d8,
      transparent: true,
      opacity: 0.34,
      depthWrite: false,
      blending: AdditiveBlending,
    });
    this.rainLines = new LineSegments(this.rainGeom, mat);
    this.rainLines.frustumCulled = false;
    this.rainLines.visible = false;
    this.rainLines.renderOrder = 6;
    ctx.scene.add(this.rainLines);
  }

  private seedRain(ctx: RenderContext): void {
    // Fill the box around wherever the camera currently is.
    const cx = ctx.camera.position.x;
    const cy = ctx.camera.position.y;
    const cz = ctx.camera.position.z;
    for (let i = 0; i < RAIN_MAX; i++) {
      const o = i * 3;
      this.rainPos[o] = cx + (Math.random() - 0.5) * RAIN_BOX_W;
      this.rainPos[o + 1] = cy + (Math.random() - 0.5) * RAIN_BOX_H;
      this.rainPos[o + 2] = cz + (Math.random() - 0.5) * RAIN_BOX_W;
    }
  }

  private updateRain(dt: number, elapsed: number): void {
    if (!this.wet || this.rainCap <= 0) {
      this.rainLines.visible = false;
      return;
    }
    this.rainLines.visible = true;
    const cap = this.rainCap;
    const cx = this.camPos.x;
    const cy = this.camPos.y;
    const cz = this.camPos.z;
    const top = cy + RAIN_BOX_H * 0.5;
    const bot = cy - RAIN_BOX_H * 0.5;
    const halfW = RAIN_BOX_W * 0.5;
    // A little slanting wind so it is not a dead vertical wall.
    const windX = Math.sin(elapsed * 0.3) * 2.2;
    const windZ = Math.cos(elapsed * 0.25) * 1.6;
    for (let i = 0; i < cap; i++) {
      const o = i * 3;
      let y = this.rainPos[o + 1]! - this.rainFall[i]! * dt;
      let x = this.rainPos[o]! + windX * dt;
      let z = this.rainPos[o + 2]! + windZ * dt;
      // Recycle drops that fell out of the box, or drifted past the camera box.
      if (y < bot) {
        y = top;
        x = cx + (Math.random() - 0.5) * RAIN_BOX_W;
        z = cz + (Math.random() - 0.5) * RAIN_BOX_W;
      }
      if (x < cx - halfW) x += RAIN_BOX_W;
      else if (x > cx + halfW) x -= RAIN_BOX_W;
      if (z < cz - halfW) z += RAIN_BOX_W;
      else if (z > cz + halfW) z -= RAIN_BOX_W;
      this.rainPos[o] = x; this.rainPos[o + 1] = y; this.rainPos[o + 2] = z;
      // Streak from head (drop) up along the fall direction.
      const vo = i * 2 * 3;
      const fall = this.rainFall[i]!;
      const inv = RAIN_STREAK / fall;
      this.rainVerts[vo] = x;
      this.rainVerts[vo + 1] = y;
      this.rainVerts[vo + 2] = z;
      this.rainVerts[vo + 3] = x - windX * inv;
      this.rainVerts[vo + 4] = y + fall * inv;
      this.rainVerts[vo + 5] = z - windZ * inv;
    }
    this.rainGeom.setDrawRange(0, cap * 2);
    (this.rainGeom.getAttribute('position') as BufferAttribute).needsUpdate = true;
  }

  private buildWetCue(ctx: RenderContext): void {
    this.wetTexture = makeDropletTexture();
    const mat = new ShaderMaterial({
      uniforms: {
        uTex: { value: this.wetTexture },
        uOffset: { value: 0 },
        uOpacity: { value: 0 },
      },
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }`,
      fragmentShader: /* glsl */ `
        uniform sampler2D uTex;
        uniform float uOffset;
        uniform float uOpacity;
        varying vec2 vUv;
        void main() {
          // Two scrolling samples so droplets never look like a static decal.
          float a = texture2D(uTex, vUv + vec2(0.0, uOffset)).a;
          float b = texture2D(uTex, vUv * 1.37 + vec2(0.11, uOffset * 0.6)).a;
          float m = max(a, b) * uOpacity;
          gl_FragColor = vec4(vec3(1.0), m);
        }`,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: AdditiveBlending,
    });
    this.wetQuad = new Mesh(new PlaneGeometry(1, 1), mat);
    this.wetQuad.frustumCulled = false;
    this.wetQuad.visible = false;
    // Draw last, on top of the 3D (the compositor still post-processes it).
    this.wetQuad.renderOrder = 1000;
    ctx.scene.add(this.wetQuad);
  }

  private updateWetCue(ctx: RenderContext, elapsed: number): void {
    const on = this.wet && this.wetActive;
    if (!on) {
      this.wetQuad.visible = false;
      return;
    }
    // A camera is not in the scene graph here, so a camera-parented quad would
    // not render; instead park a billboard just in front of the camera every
    // frame, sized to cover the current FOV. Zero-alloc (scratch only).
    const dist = 0.4;
    this.wetQuad.position.copy(this.camPos).addScaledVector(this.camFwd, dist);
    this.wetQuad.quaternion.copy(this.camQuat);
    const fovRad = (ctx.camera.fov * Math.PI) / 180;
    const h = 2 * dist * Math.tan(fovRad * 0.5) * 1.35;
    const w = h * ctx.camera.aspect;
    this.wetQuad.scale.set(w, h, 1);
    const mat = this.wetQuad.material as ShaderMaterial;
    mat.uniforms.uOffset!.value = (elapsed * 0.06) % 1;
    mat.uniforms.uOpacity!.value = 0.09; // deliberately faint — a cue, not a filter
    this.wetQuad.visible = true;
  }
}

// ---------------------------------------------------------------------------
// Static side panels — one merged mesh for both goals (4 rectangles). Their top
// and back edges match the sheets' pinned borders, so they never gap. (§6.7)
// ---------------------------------------------------------------------------

function buildGoalFrame(sign: number, halfLength: number): Group {
  const frame = new Group();
  frame.name = `goal-frame-${sign > 0 ? 'pos' : 'neg'}`;

  const postGeometry = new CylinderGeometry(GOAL_POST_RADIUS, GOAL_POST_RADIUS, GOAL_HEIGHT, 12);
  const crossbarGeometry = new CylinderGeometry(GOAL_POST_RADIUS, GOAL_POST_RADIUS, GOAL_WIDTH, 12);
  const depthGeometry = new CylinderGeometry(GOAL_POST_RADIUS * 0.72, GOAL_POST_RADIUS * 0.72, GOAL_DEPTH, 10);
  const material = new MeshStandardMaterial({
    color: GOAL_FRAME_COLOR,
    roughness: 0.26,
    metalness: 0.08,
  });
  const lineX = sign * halfLength;
  const backX = sign * (halfLength + GOAL_DEPTH);
  const halfW = GOAL_WIDTH * 0.5;
  // ⚠ AXIS MATTERS AND ONE OF THESE WAS WRONG FOR THE WHOLE PROJECT.
  // A CylinderGeometry's axis is +Y. Rotating it about Z lays it along X (the
  // pitch's LENGTH — correct for the depth rails that run from the goal line
  // back to the rear cage). Rotating about X lays it along Z (the pitch's
  // WIDTH — which is where a CROSSBAR goes). Both crossbars were rotated about
  // Z, so a 7.32 m bar lay along the pitch: measured, the frame spanned
  // x 48.84..58.16 while its net sat at 52.50..54.50, i.e. the bar stuck
  // 3.66 m INTO THE FIELD OF PLAY and the same distance out the back. That is
  // the "goal post is wrong" the owner photographed, and 3.66 is exactly half
  // the goal width, which is the fingerprint of this mistake.
  const add = (
    geometry: CylinderGeometry, x: number, y: number, z: number,
    axis: 'none' | 'alongX' | 'alongZ' = 'none',
  ): void => {
    const mesh = new Mesh(geometry, material);
    mesh.position.set(x, y, z);
    if (axis === 'alongX') mesh.rotation.z = Math.PI * 0.5;
    else if (axis === 'alongZ') mesh.rotation.x = Math.PI * 0.5;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    frame.add(mesh);
  };

  // The front frame aligns exactly with the simulation's regulation collision posts.
  add(postGeometry, lineX, GOAL_HEIGHT * 0.5, -halfW);
  add(postGeometry, lineX, GOAL_HEIGHT * 0.5, halfW);
  add(crossbarGeometry, lineX, GOAL_HEIGHT, 0, 'alongZ');   // across the mouth
  // A slim rear cage anchors the net and keeps the target readable on broadcast cameras.
  add(postGeometry, backX, GOAL_HEIGHT * 0.5, -halfW);
  add(postGeometry, backX, GOAL_HEIGHT * 0.5, halfW);
  add(crossbarGeometry, backX, GOAL_HEIGHT, 0, 'alongZ');    // across the back
  add(depthGeometry, sign * (halfLength + GOAL_DEPTH * 0.5), GOAL_HEIGHT, -halfW, 'alongX');
  add(depthGeometry, sign * (halfLength + GOAL_DEPTH * 0.5), GOAL_HEIGHT, halfW, 'alongX');
  return frame;
}

function disposeGoalFrame(frame: Group): void {
  const geometries = new Set<BufferGeometry>();
  const materials = new Set<MeshStandardMaterial>();
  frame.traverse((object) => {
    if (!(object instanceof Mesh)) return;
    geometries.add(object.geometry);
    if (object.material instanceof MeshStandardMaterial) materials.add(object.material);
  });
  for (const geometry of geometries) geometry.dispose();
  for (const material of materials) material.dispose();
}

function buildSidePanels(halfLength: number, material: MeshBasicMaterial): Mesh {
  const halfW = GOAL_WIDTH * 0.5;
  const cellM = 0.12;
  const pos: number[] = [];
  const nor: number[] = [];
  const uvs: number[] = [];
  const idx: number[] = [];
  let base = 0;
  for (const sign of [-1, 1]) {
    const frontX = sign * halfLength;
    const backX = sign * (halfLength + GOAL_DEPTH);
    for (const sz of [-1, 1]) {
      const z = sz * halfW;
      // Rectangle corners in the x-y plane at this z: front-bottom, front-top,
      // back-top, back-bottom.
      const corners: [number, number][] = [
        [frontX, 0],
        [frontX, GOAL_HEIGHT],
        [backX, GOAL_HEIGHT],
        [backX, 0],
      ];
      for (const [x, y] of corners) {
        pos.push(x, y, z);
        nor.push(0, 0, sz); // flat side normal
        uvs.push(Math.abs(x - frontX) / cellM, y / cellM);
      }
      // Two triangles; wind so the visible (inward) face is front on each side.
      if (sz < 0) {
        idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
      } else {
        idx.push(base, base + 2, base + 1, base, base + 3, base + 2);
      }
      base += 4;
    }
  }
  const geom = new BufferGeometry();
  geom.setAttribute('position', new BufferAttribute(new Float32Array(pos), 3));
  geom.setAttribute('normal', new BufferAttribute(new Float32Array(nor), 3));
  geom.setAttribute('uv', new BufferAttribute(new Float32Array(uvs), 2));
  geom.setIndex(idx);
  const mesh = new Mesh(geom, material);
  mesh.name = 'goal-net-sides';
  mesh.frustumCulled = false;
  return mesh;
}

// ---------------------------------------------------------------------------
// Procedural textures — synthesized in-code, never downloaded (§16).
// ---------------------------------------------------------------------------

/** A woven-net alpha texture: opaque threads over a transparent weave. */
function makeNetTexture(): Texture {
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const g = canvas.getContext('2d');
  const tex = new CanvasTexture(canvas);
  if (g) {
    g.clearRect(0, 0, size, size);
    g.strokeStyle = 'rgba(255,255,255,1)';
    // 7px threads / 6 cells, not 3px/8: at broadcast distance the mipmapped
    // alpha of 3px threads fell below the cutout threshold and the ENTIRE net
    // alpha-tested away (user: "there is no net", 2026-07-18). Thicker threads
    // + the lower alphaTest below keep the weave present 40m out.
    g.lineWidth = 5;
    const cells = 9;
    const step = size / cells;
    g.beginPath();
    for (let i = 0; i <= cells; i++) {
      const p = i * step;
      g.moveTo(p, 0); g.lineTo(p, size);
      g.moveTo(0, p); g.lineTo(size, p);
    }
    g.stroke();
  }
  tex.wrapS = RepeatWrapping;
  tex.wrapT = RepeatWrapping;
  tex.colorSpace = SRGBColorSpace;
  // No mipmaps: with an alphaTest cutout, mip-averaged thread alpha still
  // erases the weave at broadcast range (40m+) even after thickening — the
  // second "there is no net" report was from open play, not close-ups. Plain
  // linear sampling keeps threads present at ANY distance; slight shimmer on
  // a white net is invisible in practice.
  tex.generateMipmaps = false;
  tex.minFilter = LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

/** Scattered droplet highlights for the screen-space wetness cue. */
function makeDropletTexture(): Texture {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const g = canvas.getContext('2d');
  const tex = new CanvasTexture(canvas);
  if (g) {
    g.clearRect(0, 0, size, size);
    for (let i = 0; i < 140; i++) {
      const x = Math.random() * size;
      const y = Math.random() * size;
      const r = 1 + Math.random() * 3.5;
      const grd = g.createRadialGradient(x, y, 0, x, y, r);
      grd.addColorStop(0, 'rgba(255,255,255,0.9)');
      grd.addColorStop(1, 'rgba(255,255,255,0)');
      g.fillStyle = grd;
      g.beginPath();
      g.arc(x, y, r, 0, Math.PI * 2);
      g.fill();
    }
  }
  tex.wrapS = RepeatWrapping;
  tex.wrapT = RepeatWrapping;
  tex.needsUpdate = true;
  return tex;
}

/**
 * @param opts.drawGoalFrames pass FALSE when the stadium models its own goal
 *   frames (the V5 bowl does) — see `VfxSystem.drawGoalFrames`.
 */
export function createVfxSystem(opts: { drawGoalFrames?: boolean } = {}): RenderSubsystem {
  return new VfxSystem(opts);
}

// Keep the `Object3D` import meaningful for readers grepping the scene graph:
// every mesh above is added to `ctx.scene` (an Object3D tree) and removed on
// dispose / pitch rebuild.
export type { Object3D };
